import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Wallet, Bell, TrendingDown, Calendar, DollarSign } from 'lucide-react';

export default function LiveBudgetsSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-24 lg:py-32 overflow-hidden"
    >
      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="glass-card p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-sm text-dark-500 mb-1">Monthly Budget</div>
                    <div className="text-2xl font-bold text-white">$25,000</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-dark-500 mb-1">Remaining</div>
                    <div className="text-2xl font-bold text-cyan-400">$8,420</div>
                  </div>
                </div>

                <div className="h-3 bg-dark-900/50 rounded-full overflow-hidden mb-6">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full"
                    style={{ width: '66%' }}
                  />
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-dark-400">Daily Target</span>
                    <span className="text-white">$833</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-dark-400">Today's Spend</span>
                    <span className="text-green-400">$742</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-dark-400">Pacing</span>
                    <span className="text-cyan-400">On track ✓</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <Bell className="w-4 h-4 text-yellow-400 mt-0.5" />
                    <div className="text-sm">
                      <span className="text-white font-medium">Budget Alert</span>
                      <p className="text-dark-400">Campaign "Summer Sale" is pacing 15% above target</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                    <TrendingDown className="w-4 h-4 text-cyan-400 mt-0.5" />
                    <div className="text-sm">
                      <span className="text-white font-medium">Optimization</span>
                      <p className="text-dark-400">AI reduced bids on 3 underperforming campaigns</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                Budget Management
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Never overspend{' '}
                <span className="text-cyan-400 cyan-glow">again.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Real-time budget tracking with intelligent pacing and automatic alerts. Stay on target without constant monitoring.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Wallet, title: 'Multi-campaign Budgets', desc: 'Set and track budgets across all campaign types' },
                  { icon: Calendar, title: 'Dayparting Controls', desc: 'Adjust spend by day of week and time of day' },
                  { icon: DollarSign, title: 'Smart Pacing', desc: 'AI adjusts daily spend to hit monthly targets' },
                  { icon: Bell, title: 'Proactive Alerts', desc: 'Get notified before budgets are exceeded' },
                ].map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                      <p className="text-sm text-dark-400">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
